// ---------------------------------------------------------------------------
// ZIP PREFIX -> STATE (USPS 3-digit sectional centres), 2026-10-09.
// ---------------------------------------------------------------------------
// SINGLE SOURCE for the browser. The database copy is the table
// `mkt_zip_prefix_states`, seeded from exactly these ranges, and is what the
// trigger `trg_mkt_listings_validate_zip` enforces (only when the ZIP changes).
// If you change a range here, change the SQL seed too (see
// docs/GEO_ZIP_CLEANUP_20261009.md, section D).
//
// Rules:
//   * A prefix may map to more than one state (e.g. 967 = HI + AS). A ZIP
//     matches if the listing state is ANY of the mapped states.
//   * Prefixes not listed (unassigned, e.g. 004, 428, 694-699) have no rule:
//     we never claim a mismatch we cannot prove.
//   * Known oddities are explicit: 055 = MA (Andover IRS), 733 = TX (Austin
//     IRS), 885 = TX (El Paso), 569 = DC, 005 = NY (Holtsville).
// ---------------------------------------------------------------------------

/** [firstPrefix, lastPrefix, states] — inclusive, 3-digit numbers. */
export const ZIP_PREFIX_RANGES: ReadonlyArray<readonly [number, number, readonly string[]]> = [
  [5, 5, ['NY']],
  [6, 7, ['PR']], [8, 8, ['VI']], [9, 9, ['PR']],
  [10, 27, ['MA']],
  [28, 29, ['RI']],
  [30, 38, ['NH']],
  [39, 49, ['ME']],
  [50, 54, ['VT']], [55, 55, ['MA']], [56, 59, ['VT']],
  [60, 69, ['CT']],
  [70, 89, ['NJ']],
  [90, 99, ['AE']],
  [100, 149, ['NY']],
  [150, 196, ['PA']],
  [197, 199, ['DE']],
  [200, 200, ['DC']], [201, 201, ['VA']], [202, 205, ['DC']], [206, 219, ['MD']],
  [220, 246, ['VA']],
  [247, 268, ['WV']],
  [270, 289, ['NC']],
  [290, 299, ['SC']],
  [300, 319, ['GA']],
  [320, 339, ['FL']],
  [340, 340, ['AA']],
  [341, 349, ['FL']],
  [350, 369, ['AL']],
  [370, 385, ['TN']],
  [386, 397, ['MS']],
  [398, 399, ['GA']],
  [400, 427, ['KY']],
  [430, 459, ['OH']],
  [460, 479, ['IN']],
  [480, 499, ['MI']],
  [500, 528, ['IA']],
  [530, 549, ['WI']],
  [550, 567, ['MN']],
  [569, 569, ['DC']],
  [570, 577, ['SD']],
  [580, 588, ['ND']],
  [590, 599, ['MT']],
  [600, 629, ['IL']],
  [630, 658, ['MO']],
  [660, 679, ['KS']],
  [680, 693, ['NE']],
  [700, 714, ['LA']],
  [716, 729, ['AR']],
  [730, 732, ['OK']], [733, 733, ['TX']], [734, 749, ['OK']],
  [750, 799, ['TX']],
  [800, 816, ['CO']],
  [820, 831, ['WY']],
  [832, 838, ['ID']],
  [840, 847, ['UT']],
  [850, 865, ['AZ']],
  [870, 884, ['NM']], [885, 885, ['TX']],
  [889, 898, ['NV']],
  [900, 961, ['CA']],
  [962, 966, ['AP']],
  [967, 967, ['HI', 'AS']], [968, 968, ['HI']],
  [969, 969, ['GU', 'MP', 'PW', 'FM', 'MH']],
  [970, 979, ['OR']],
  [980, 994, ['WA']],
  [995, 999, ['AK']],
];

/** States a ZIP's 3-digit prefix belongs to; [] when the prefix has no rule. */
export const statesForZip = (zip: string | null | undefined): string[] => {
  const d = (zip || '').trim().slice(0, 5);
  if (!/^\d{5}$/.test(d)) return [];
  const p = Number(d.slice(0, 3));
  const hit = ZIP_PREFIX_RANGES.find(([a, b]) => p >= a && p <= b);
  return hit ? [...hit[2]] : [];
};

/**
 * True only when we can PROVE the ZIP is outside the selected state:
 * 5-digit ZIP, 2-letter state, mapped prefix, and the state is not among the
 * mapped states. Mirrors SQL mkt_zip_state_mismatch().
 */
export const zipStateMismatch = (zip: string | null | undefined, state: string | null | undefined): boolean => {
  const st = (state || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(st)) return false;
  const states = statesForZip(zip);
  return states.length > 0 && !states.includes(st);
};
